function getEventPosition(e) {
    const target = e.type.startsWith('touch')
        ? e.changedTouches[0]
        : e;
    return { x: target.clientX, y: target.clientY };
}
/**
 * A mouse event handler which can start a drag and callback when move or end during the drag.
 */
export function handleDrag(startEvent, { threshold = 4, onStart, onMove, onEnd, cursor }) {
    const startPosition = getEventPosition(startEvent);
    let lastPosition = startPosition;
    const { target } = startEvent;
    const targetCursor = cursor ??
        (target instanceof Element
            ? window.getComputedStyle(target).cursor
            : undefined);
    let started = threshold <= 0;
    let stopped = false;
    let mask;
    function getUpdatedPosition(x, y) {
        const result = {
            clientX: x,
            clientY: y,
            deltaX: x - startPosition.x,
            deltaY: y - startPosition.y,
            dX: x - lastPosition.x,
            dY: y - lastPosition.y,
        };
        lastPosition = { x, y };
        return result;
    }
    function handleStart(e) {
        const { x, y } = getEventPosition(e);
        started = true;
        mask = document.createElement('div');
        const { style } = mask;
        style.position = 'fixed';
        style.top = '0';
        style.bottom = '0';
        style.left = '0';
        style.right = '0';
        style.zIndex = '9999';
        style.cursor = targetCursor ?? '';
        style.userSelect = 'none';
        document.body.appendChild(mask);
        onStart?.({
            event: e,
            ...getUpdatedPosition(x, y),
        });
    }
    function handleMove(e) {
        const { x, y } = getEventPosition(e);
        if (started) {
            onMove?.({
                event: e,
                ...getUpdatedPosition(x, y),
            });
            e.preventDefault();
        }
        else if (Math.abs(x - startPosition.x) + Math.abs(y - startPosition.y) >=
            threshold) {
            handleStart(e);
        }
    }
    function handleSelectStart(e) {
        e.preventDefault();
    }
    function handleEnd(e) {
        if (stopped) {
            return;
        }
        stopped = true;
        const { x, y } = e ? getEventPosition(e) : lastPosition;
        if (mask) {
            document.body.removeChild(mask);
            mask = undefined;
        }
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', handleEnd);
        document.removeEventListener('pointercancel', handleEnd);
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);
        document.removeEventListener('selectstart', handleSelectStart);
        if (started) {
            onMove?.({
                event: e,
                ...getUpdatedPosition(x, y),
            });
            onEnd?.({
                event: e,
                ...getUpdatedPosition(x, y),
            });
            e?.preventDefault();
        }
    }
    if (startEvent.type.startsWith('pointer')) {
        document.addEventListener('pointermove', handleMove);
        document.addEventListener('pointerup', handleEnd);
        document.addEventListener('pointercancel', handleEnd);
    }
    if (startEvent.type.startsWith('mouse')) {
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleEnd);
    }
    if (startEvent.type.startsWith('touch')) {
        document.addEventListener('touchmove', handleMove, { passive: false });
        document.addEventListener('touchend', handleEnd, { passive: false });
        document.addEventListener('touchcancel', handleEnd, { passive: false });
    }
    document.addEventListener('selectstart', handleSelectStart);
    if (started) {
        handleStart(startEvent);
        startEvent.preventDefault();
    }
    return {
        stop: () => {
            handleEnd();
        },
    };
}
